// src/App.js

import { useEffect, useMemo, useState } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import "./tailwind.css";

import Header from "./components/Header";
import GuestQuiz from "./components/GuestQuiz";
import AdminDashboard from "./components/AdminDashboard";
import LoadingState from "./components/ui/LoadingState";
import GuestMode from "./pages/GuestMode";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import MoodBoard from "./pages/MoodBoard";
import Profile from "./pages/Profile";
import Recommendations from "./pages/Recommendations";
import SavedShows from "./pages/SavedShows";
import SearchDiscover from "./pages/SearchDiscover";
import ShowDetails from "./pages/ShowDetails";
import Signup from "./pages/Signup";
import Trending from "./pages/Trending";
import { getCurrentUser, logout } from "./utils/auth";

function isAdminUser(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : [];

  return Boolean(
    user?.is_superuser ||
      user?.is_admin ||
      user?.role === "admin" ||
      roles.includes("admin")
  );
}

function CheckingAccess() {
  return (
    <main className="px-4 py-10">
      <LoadingState title="Checking access" message="Confirming your account access." />
    </main>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const refreshCurrentUser = async () => {
    const currentUser = await getCurrentUser();
    setUser(currentUser);
    return currentUser;
  };

  useEffect(() => {
    let active = true;

    getCurrentUser()
      .then((currentUser) => {
        if (active) setUser(currentUser);
      })
      .catch(() => {
        if (active) setUser(null);
      })
      .finally(() => {
        if (active) setAuthReady(true);
      });

    return () => {
      active = false;
    };
  }, []);

  const isAdmin = useMemo(() => isAdminUser(user), [user]);

  const handleLogout = async () => {
    await logout();
    setUser(null);
  };

  return (
    <Router>
      <div className="hookd-page min-h-screen text-hookd-cream">
        <Header user={user} isAdmin={isAdmin} onLogout={handleLogout} />

        <main className="pb-24 md:pb-0">
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/guest" element={<Navigate to="/" replace />} />
            <Route path="/quiz" element={<GuestQuiz />} />
            <Route path="/recommendations" element={<Recommendations />} />
            <Route path="/mood-board" element={<MoodBoard />} />
            <Route path="/mood-boards" element={<Navigate to="/mood-board" replace />} />
            <Route path="/trending" element={<Trending />} />
            <Route path="/discover" element={<SearchDiscover />} />
            <Route path="/shows/:showKey" element={<ShowDetails />} />
            <Route path="/guest-mode" element={<GuestMode />} />
            <Route path="/login" element={<Login onLogin={refreshCurrentUser} />} />
            <Route path="/signup" element={<Signup onSignup={setUser} />} />
            <Route
              path="/profile"
              element={
                !authReady ? (
                  <CheckingAccess />
                ) : user ? (
                  <Profile />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/saved-shows"
              element={
                !authReady ? (
                  <CheckingAccess />
                ) : user ? (
                  <SavedShows />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin"
              element={
                !authReady ? (
                  <CheckingAccess />
                ) : isAdmin ? (
                  <AdminDashboard />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}
